(() => {
  if (window.cryoSourceReviewsBooted) return;
  window.cryoSourceReviewsBooted = true;
  const text = value => typeof value === 'string' ? value : '';
  const safeUrl = value => {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
    } catch { return null; }
  };
  const el = (tag, className, content) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (content !== undefined) element.textContent = content;
    return element;
  };
  const initialize = async root => {
    if (root.dataset.cryoReady) return;
    root.dataset.cryoReady = 'loading';
    const loading = root.querySelector('[data-cryo-loading]');
    const labels = JSON.parse(root.querySelector('[data-cryo-labels]').textContent);
    try {
      const response = await fetch(root.dataset.reviewsUrl, {credentials:'omit'});
      if (!response.ok) throw new Error('Review asset unavailable');
      const payload = await response.json();
      if (!Array.isArray(payload.reviews) || !payload.reviews.length) throw new Error('No source reviews');
      const seen = new Set();
      const reviews = payload.reviews.map((record, index) => ({
        ...record,
        id: text(record.id),
        rating: Number(record.rating),
        author: text(record.author),
        title: text(record.title),
        text: text(record.text),
        sourceProduct: text(record.sourceProduct),
        location: text(record.location),
        locale: text(record.locale),
        photos: (Array.isArray(record.photos) ? record.photos : []).map(photo => {
          const value = typeof photo === 'string' ? {url:photo} : photo;
          return {url:safeUrl(value?.url), thumbnail:safeUrl(value?.thumbnail || value?.url), caption:text(value?.caption)};
        }).filter(photo => photo.url && photo.thumbnail),
        sourceIndex:index,
      })).filter(record => {
        if (!record.id || !record.text || !Number.isInteger(record.rating) || record.rating < 1 || record.rating > 5 || seen.has(record.id)) return false;
        seen.add(record.id);
        return true;
      });
      if (reviews.length !== payload.reviews.length) throw new Error('Invalid or duplicate review records');
      const number = new Intl.NumberFormat('de-DE', {maximumFractionDigits:0});
      const average = reviews.reduce((sum, item) => sum + item.rating, 0) / reviews.length;
      root.querySelector('[data-cryo-average]').textContent = new Intl.NumberFormat('de-DE', {minimumFractionDigits:1,maximumFractionDigits:1}).format(average);
      root.querySelector('[data-cryo-total]').textContent = `${number.format(reviews.length)} ${labels.reviews}`;
      const photoCount = reviews.reduce((sum, item) => sum + item.photos.length, 0);
      root.querySelector('[data-cryo-photo-total]').textContent = `${number.format(photoCount)} ${labels.photos}`;
      const sourceName = text(payload.source?.name);
      root.querySelector('[data-cryo-source-note]').textContent = `${labels.source}: ${sourceName}. ${labels.provenance}`;
      const distribution = root.querySelector('[data-cryo-distribution]');
      const ratingFilter = root.querySelector('[data-cryo-rating-filter]');
      const sort = root.querySelector('[data-cryo-sort]');
      const onlyPhotos = root.querySelector('[data-cryo-only-photos]');
      const status = root.querySelector('[data-cryo-status]');
      const grid = root.querySelector('[data-cryo-grid]');
      const previous = root.querySelector('[data-cryo-prev]');
      const next = root.querySelector('[data-cryo-next]');
      const pageLabel = root.querySelector('[data-cryo-page]');
      const empty = root.querySelector('[data-cryo-empty]');
      const dialog = root.querySelector('[data-cryo-zoom]');
      const zoomImage = root.querySelector('[data-cryo-zoom-image]');
      const closeZoom = root.querySelector('[data-cryo-zoom-close]');
      const ratingButtons = [];
      let page = 0;
      let photoTrigger;
      const pageSize = 8;
      const openPhoto = (photo, author, trigger) => {
        photoTrigger = trigger;
        if (typeof dialog.showModal !== 'function') {
          window.open(photo.url, '_blank', 'noopener,noreferrer');
          return;
        }
        zoomImage.src = photo.url;
        zoomImage.alt = photo.caption || `${labels.customerPhoto} · ${author}`;
        dialog.showModal();
        closeZoom.focus();
      };
      const card = review => {
        const article = el('article', 'cryo-review');
        article.id = `${root.id}-review-${review.sourceIndex + 1}`;
        const top = el('div', 'cryo-review-top');
        const author = review.author || labels.authorUnknown;
        top.append(el('strong', 'cryo-review-author', author));
        const stars = el('span', 'cryo-review-stars', '★'.repeat(review.rating) + '☆'.repeat(5-review.rating));
        stars.setAttribute('aria-label', `${review.rating} ${labels.outOfFive}`);
        top.append(stars);
        article.append(top);
        if (review.verifiedPurchase === true || review.incentivized === true) {
          const badges = el('div', 'cryo-review-badges');
          if (review.verifiedPurchase === true) badges.append(el('span','cryo-review-badge',labels.verified));
          if (review.incentivized === true) badges.append(el('span','cryo-review-badge cryo-review-badge-incentive',labels.incentivized));
          article.append(badges);
        }
        if (review.title) article.append(el('h3','',review.title));
        article.append(el('p','cryo-review-text',review.text));
        const metadata = [];
        if (review.sourceProduct) metadata.push(`${labels.reviewedProduct}: ${review.sourceProduct}`);
        if (review.location) metadata.push(`${labels.region}: ${review.location}`);
        if (review.locale) metadata.push(`${labels.locale}: ${review.locale}`);
        if (metadata.length) article.append(el('p','cryo-review-meta',metadata.join(' · ')));
        if (review.photos.length) {
          const photos = el('div','cryo-review-images');
          review.photos.forEach((photo,index) => {
            const button = el('button','cryo-review-photo');
            button.type = 'button';
            button.setAttribute('aria-label',`${labels.showPhoto} ${index+1} · ${author}`);
            const image = el('img');
            image.src = photo.thumbnail;
            image.alt = photo.caption || `${labels.customerPhoto} ${index+1} · ${author}`;
            image.width = 180;
            image.height = 180;
            image.loading = 'lazy';
            image.decoding = 'async';
            button.append(image);
            button.addEventListener('click',() => openPhoto(photo,author,button));
            photos.append(button);
          });
          article.append(photos);
        }
        return article;
      };
      const render = (move=false) => {
        let filtered = reviews.filter(review => (ratingFilter.value === 'all' || review.rating === Number(ratingFilter.value)) && (!onlyPhotos.checked || review.photos.length > 0));
        if (sort.value === 'photos') filtered = [...filtered].sort((a,b) => Number(b.photos.length > 0) - Number(a.photos.length > 0) || a.sourceIndex - b.sourceIndex);
        const pageCount = Math.max(1,Math.ceil(filtered.length/pageSize));
        page = Math.max(0,Math.min(page,pageCount-1));
        grid.replaceChildren(...filtered.slice(page*pageSize,(page+1)*pageSize).map(card));
        status.textContent = `${filtered.length ? page*pageSize+1 : 0}–${Math.min((page+1)*pageSize,filtered.length)} / ${number.format(filtered.length)} ${labels.reviews}`;
        pageLabel.textContent = `${page+1} / ${pageCount}`;
        previous.disabled = page === 0;
        next.disabled = page >= pageCount-1;
        empty.hidden = filtered.length !== 0;
        ratingButtons.forEach(button => button.setAttribute('aria-pressed',String(button.dataset.rating === ratingFilter.value)));
        if (move) root.querySelector('.cryo-reviews-tools').scrollIntoView({block:'start',behavior:'instant'});
      };
      for (let rating=5; rating>=1; rating--) {
        const count = reviews.filter(review => review.rating === rating).length;
        const button = el('button','cryo-reviews-rating-row');
        button.type = 'button';
        button.dataset.rating = String(rating);
        button.setAttribute('aria-pressed','false');
        button.setAttribute('aria-controls',grid.id);
        button.setAttribute('aria-label',`${rating} ${rating === 1 ? labels.star : labels.stars}: ${count}`);
        button.append(el('span','',`${rating} ★`));
        const meter = el('meter');
        meter.min=0;meter.max=reviews.length;meter.value=count;
        meter.setAttribute('aria-hidden','true');
        button.append(meter,el('span','',number.format(count)));
        button.addEventListener('click',() => {ratingFilter.value = String(rating);page=0;render();});
        distribution.append(button);
        ratingButtons.push(button);
      }
      [ratingFilter,sort,onlyPhotos].forEach(control => control.addEventListener('change',() => {page=0;render();}));
      previous.addEventListener('click',() => {page--;render(true);});
      next.addEventListener('click',() => {page++;render(true);});
      closeZoom.addEventListener('click',() => dialog.close());
      dialog.addEventListener('click',event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
      });
      dialog.addEventListener('close',() => {zoomImage.removeAttribute('src');photoTrigger?.focus();});
      render();
      loading.hidden = true;
      root.querySelector('[data-cryo-content]').hidden = false;
      root.dataset.cryoReady = 'true';
    } catch {
      root.dataset.cryoReady = 'error';
      loading.textContent = labels.loadError;
    }
  };
  const scan = context => context.querySelectorAll('[data-cryo-reviews]').forEach(initialize);
  scan(document);
  document.addEventListener('shopify:section:load',event => scan(event.target));
})();
